import {
  schAloftPriceJSON,
  schAloftTimeJSON,
  schAnalysisPeriod,
  schDateOfSaleMethod,
  schListToSaleMethod,
  schMLSID,
} from '@aloft/toolkit-shared';

import { z } from 'zod';

// NOTE: These are copies of the `marketAdjustments` order attachment schemas
// with certain properties replaced with form-friendly string schema properties.
//
// `stateToForm`, `formManualAdjustmentsToState`, and `formMarketAdjustmentsToState`
// convert Redux state to form input and form input back to Redux when we need to
// use the form values.
//
// There's probably a better way to do this but there's not a lot of time to
// figure that out right now.

const schFormManualAdjustments = z.object({
  concessions: z.string().transform((x) => (!x ? '0' : x)),
  termsOfSale: z.string().transform((x) => (!x ? '0' : x)),
});
export type FormManualAdjustments = z.infer<typeof schFormManualAdjustments>;

const schZillowData = z.object({
  zip: z.string(),
  updated_date: z.string(),
  monthly_trends: z.object({
    historical_data: z.array(
      z.object({
        year: z.number(),
        month: z.number(),
        typicalHomeValue: schAloftPriceJSON,
        numCloses: z.number().optional(),
        sourceMLSes: schMLSID.array().optional(),
        medianListPrice: schAloftPriceJSON.optional(),
        medianClosePriceOverListPrice: z.number().optional(),
        medianDaysOnMarket: z.number().optional(),
      }),
    ),
    one_month_projection: schAloftPriceJSON.optional(),
  }),
});

// NOTE: The Zillow form state shouldn't need anything other than `analysisPeriod`
// right now, but including everything simplifies the form state mapping.
const schZillowState = z.object({
  zip: z.string(),
  analysisPeriod: schAnalysisPeriod,
  trends: z.array(
    z.object({
      data: schZillowData,
      timestamp: schAloftTimeJSON,
    }),
  ),
});

// NOTE: This is a copy of the schema from shared; the import is breaking the build
// for some reason. I'll (maybe) investigate this later.
const ISO_MONTH_REGEX = /^([0-9]{4})-(1[0-2]|0[1-9])$/;
export const schIsoMonth = z.string().regex(ISO_MONTH_REGEX).brand('IsoMonth');

const schCustomMonthlyPriceState = z.object({
  type: z.enum(['typicalHomeValue']),
  analysisPeriod: schAnalysisPeriod,
  trendsAsPercentages: z.boolean().optional().default(false),
  trends: z.array(
    z.object({
      month: schIsoMonth,
      value: z.string(),
    }),
  ),
});

const schFormMarketAdjustments = z.object({
  version: z.literal(1),
  cachedForms: z.object({
    dateOfSale: z.object({
      selectedMethod: schDateOfSaleMethod,
      methodState: z.object({
        zillow: schZillowState.optional(),
        customMonthlyRate: z.object({
          rate: z.string(),
        }),
        customMonthlyPrice: schCustomMonthlyPriceState,
      }),
    }),
    listToSale: z.object({
      selectedMethod: schListToSaleMethod,
      methodState: z
        .object({
          customRatio: z.object({
            ratio: z.string(),
          }),
        })
        .partial(),
    }),
  }),
});

export type MarketAdjustmentsModalForm = z.infer<typeof schMarketAdjustmentsModalForm>;
export const schMarketAdjustmentsModalForm = z.object({
  manualAdjustments: z.record(schFormManualAdjustments),
  marketAdjustments: schFormMarketAdjustments,
  commentary: z.string().or(z.undefined()),
});
